import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { parseLocalDate, todayLocal, daysBetween, diffYMD, nextBirthday, weekday, compare, businessDays } from "./dates";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";

const TOOL_INFO_CONTENT = {
  what: `Calculates exact age in years, months, and days from a birth date, or the number of days between any two dates — more precise than the rough mental math of subtracting years, which doesn't account for whether the birthday has actually happened yet this year.`,
  how: `Enter a birth date (or two dates for a general date difference), and the calculated age or interval appears immediately.`,
  tip: `This is also useful for far more than birthdays — counting days until a deadline, the exact duration of an event, or how many days pregnant someone is all work the same way, just by entering the relevant two dates instead of a birth date and today.`,
};

export default function AgeCalculator() {
  const [mode, setMode] = useState<"age" | "between">("age");
  const [birthDate, setBirthDate] = useState("");
  const today = todayLocal();
  const todayStr = `${today.y}-${String(today.m).padStart(2, "0")}-${String(today.d).padStart(2, "0")}`;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(todayStr);
  const [includeEnd, setIncludeEnd] = useState(false);
  const [bizOnly, setBizOnly] = useState(false);

  const age = useMemo(() => {
    const dob = parseLocalDate(birthDate);
    if (!dob) return null;
    const t = todayLocal();
    const delta = daysBetween(t, dob);
    if (delta > 0) return { future: true as const, days: delta };
    const { years, months, days } = diffYMD(dob, t);
    const totalDays = daysBetween(dob, t);
    const nb = nextBirthday(dob, t);
    const untilBirthday = daysBetween(t, nb.date);
    return {
      future: false as const, years, months, days, totalDays, totalWeeks: Math.floor(totalDays / 7), totalMonths: years * 12 + months,
      bornOn: weekday(dob), untilBirthday, nextOn: weekday(nb.date), usedMarch1: nb.usedMarch1,
    };
  }, [birthDate]);

  const between = useMemo(() => {
    const a = parseLocalDate(from), b = parseLocalDate(to);
    if (!a || !b) return null;
    const reversed = compare(a, b) > 0;
    const [lo, hi] = reversed ? [b, a] : [a, b];
    const { years, months, days } = diffYMD(lo, hi);
    const total = daysBetween(lo, hi) + (includeEnd ? 1 : 0);
    return { reversed, years, months, days, total, weeks: Math.floor(total / 7), rem: total % 7, biz: businessDays(lo, hi, includeEnd) };
  }, [from, to, includeEnd]);

  const { blocked } = useLogToolUsage(mode === "age" ? age : between);

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "age" ? "default" : "outline"} onClick={() => setMode("age")}>Age</Button>
          <Button size="sm" variant={mode === "between" ? "default" : "outline"} onClick={() => setMode("between")}>Days between two dates</Button>
        </div>

        {mode === "age" ? (
          <>
            <div><Label>Date of Birth</Label><Input aria-label="Date of Birth" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} /></div>
            {age?.future && !blocked && (
              <p className="p-4 rounded-lg border text-sm" data-testid="age-future">That date is in the future: {age.days.toLocaleString()} day{age.days === 1 ? "" : "s"} from today.</p>
            )}
            {age && !age.future && !blocked && (
              <div className="space-y-4">
                <div className="p-6 rounded-xl border bg-card text-center">
                  <p className="text-4xl font-bold text-primary" data-testid="age-years">{age.years}</p>
                  <p className="text-sm text-muted-foreground" data-testid="age-ymd">years, {age.months} months, {age.days} days</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg border text-center"><p className="text-lg font-semibold">{age.totalMonths.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Months</p></div>
                  <div className="p-3 rounded-lg border text-center"><p className="text-lg font-semibold">{age.totalWeeks.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Weeks</p></div>
                  <div className="p-3 rounded-lg border text-center"><p className="text-lg font-semibold" data-testid="age-days">{age.totalDays.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Days</p></div>
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  Born on a {age.bornOn}. Next birthday {age.untilBirthday === 0 ? "is today" : `in ${age.untilBirthday} day${age.untilBirthday === 1 ? "" : "s"}`} (a {age.nextOn}).
                  {age.usedMarch1 && " In years that are not leap years, 29 February birthdays are counted on 1 March."}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input aria-label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
              <div><Label>To</Label><Input aria-label="To" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
            </div>
            <div className="flex flex-wrap gap-5 text-sm">
              <div className="flex items-center gap-2"><Switch checked={includeEnd} onCheckedChange={setIncludeEnd} aria-label="Include the end date" /><Label className="text-sm">Include the end date</Label></div>
              <div className="flex items-center gap-2"><Switch checked={bizOnly} onCheckedChange={setBizOnly} aria-label="Business days only" /><Label className="text-sm">Business days only (Mon–Fri)</Label></div>
            </div>
            {between && !blocked && (
              <div className="p-6 rounded-xl border bg-card text-center space-y-1" data-testid="between-result">
                <p className="text-4xl font-bold text-primary">{(bizOnly ? between.biz : between.total).toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">{bizOnly ? "business days" : "days"}{between.reversed ? " (the dates were swapped)" : ""}</p>
                {!bizOnly && <p className="text-sm text-muted-foreground">{between.years} years, {between.months} months, {between.days} days · {between.weeks} weeks{between.rem ? ` and ${between.rem} day${between.rem === 1 ? "" : "s"}` : ""}</p>}
                {bizOnly && <p className="text-xs text-muted-foreground">Public holidays are not excluded.</p>}
              </div>
            )}
          </>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
